import "./OCRMSADecisionReviewForm.scss";

import Button from "components/Button/Button";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { DatePicker, MaskedInput, Select, TextArea } from "components/inputs";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import {
  useCreateMsaDecisionLineItemMutation,
  useDeleteMsaDecisionLineItemByIdMutation,
  usePatchMSADecisionMutation,
} from "services/msa-api";
import {
  usePatchReportPrescriptionAllocationByIdMutation,
  usePatchReportTreatmentAllocationByIdMutation,
} from "services/reports-api";
import type {
  MSADecisionReviewLetterLineItem,
  MSADecisionReviewPayload,
  MSADecisionType,
} from "types/DataTypes";
import { STRING_TO_DATE } from "utils/date-utils";
import { formatCurrency } from "utils/format-utils";
import { useSelector } from "utils/redux";

type OCRMSADecisionReviewFormProps = {
  msaId: string;
  decision: MSADecisionType;
  reviewPayload: MSADecisionReviewPayload;
  onSaved: () => void;
  onCancel: () => void;
};

type RowMatch = {
  matchedAllocationId: number | null;
  matchType: "ndc_exact" | "name_fuzzy" | null;
  confidence: number | null;
};

type LetterRow = {
  item: MSADecisionReviewLetterLineItem;
  rowKey: string;
  match: RowMatch;
};

// Prescriptions get an NDC exact match, treatment items get a name similarity match, set by the backend.
const resolveRowMatch = (item: MSADecisionReviewLetterLineItem): RowMatch => ({
  matchedAllocationId: item.matched_allocation_id ?? null,
  matchType: item.match_type ?? null,
  confidence: item.confidence ?? null,
});

// Typed against decision_fields's own keys, not a plain Record<string, string>, so the type checker flags this
// map if a new field is ever added to decision_fields and forgotten here.
const REVIEWABLE_FIELD_LABELS: Record<
  Exclude<
    keyof MSADecisionReviewPayload["decision_fields"],
    "decision_type_raw_text"
  >,
  string
> = {
  case_control_number: "Case control number",
  claimant_name: "Claimant name",
  medicare_id: "Medicare ID",
  date_of_injury: "Date of injury",
  decision_type: "Decision type",
  decision_date: "Decision date",
  amount_total: "Approval amount total",
  medical_total: "Medical total",
  rx_total: "Rx total",
  cms_rationale: "CMS rationale",
};

const OCRMSADecisionReviewForm = ({
  msaId,
  decision,
  reviewPayload,
  onSaved,
  onCancel,
}: OCRMSADecisionReviewFormProps) => {
  const constants = useSelector((state) => state.shared.constants);
  const { decision_fields: fields } = reviewPayload;

  // reviewPayload is written by the backend before this form ever mounts, so these are just initial values.
  const [decisionType, setDecisionType] = useState(fields.decision_type);
  const [decisionDate, setDecisionDate] = useState(fields.decision_date);
  const [amountTotal, setAmountTotal] = useState(fields.amount_total || "");
  // medical_total/rx_total save onto MSADecision here, though CMSDecision.tsx shows sums of line items instead.
  const [medicalTotal, setMedicalTotal] = useState(fields.medical_total || "");
  const [rxTotal, setRxTotal] = useState(fields.rx_total || "");
  const [cmsRationale, setCmsRationale] = useState(fields.cms_rationale || "");
  const [isSaving, setIsSaving] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  const [patchMSADecision] = usePatchMSADecisionMutation();
  const [patchTreatmentAllocation] =
    usePatchReportTreatmentAllocationByIdMutation();
  const [patchPrescriptionAllocation] =
    usePatchReportPrescriptionAllocationByIdMutation();
  const [createDecisionLineItem] = useCreateMsaDecisionLineItemMutation();
  const [deleteMsaDecisionLineItemById] =
    useDeleteMsaDecisionLineItemByIdMutation();

  const treatmentRows: LetterRow[] = useMemo(
    () =>
      // Index in the key, not just the name, since a letter can legitimately list the same service name twice.
      reviewPayload.treatment_line_items.map((item, index) => ({
        item,
        rowKey: `treatment-${index}-${item.name}`,
        match: resolveRowMatch(item),
      })),
    [reviewPayload],
  );

  const prescriptionRows: LetterRow[] = useMemo(
    () =>
      reviewPayload.prescription_line_items.map((item, index) => ({
        item,
        rowKey: `prescription-${index}-${item.name}`,
        match: resolveRowMatch(item),
      })),
    [reviewPayload],
  );

  // One per proposed allocation already on this decision, excluding new items a previous Apply already added.
  const proposedTreatmentRows = useMemo(
    () => decision.line_items.filter((li) => li.proposed_treatment_allocation),
    [decision],
  );
  const proposedPrescriptionRows = useMemo(
    () =>
      decision.line_items.filter((li) => li.proposed_prescription_allocation),
    [decision],
  );

  // Line items with a decision but no proposal, since CMS approved something that was never asked for.
  const decisionOnlyTreatmentRows = useMemo(
    () =>
      decision.line_items.filter(
        (li) =>
          li.decision_treatment_allocation && !li.proposed_treatment_allocation,
      ),
    [decision],
  );
  const decisionOnlyPrescriptionRows = useMemo(
    () =>
      decision.line_items.filter(
        (li) =>
          li.decision_prescription_allocation &&
          !li.proposed_prescription_allocation,
      ),
    [decision],
  );

  // Which letter row is picked for each proposed line item, keyed by the line item's id. Seeded once from the
  // backend's auto-match, then owned entirely by this form, since selecting a dropdown is itself the apply action.
  const [assignments, setAssignments] = useState<Record<number, string | null>>(
    () => {
      const initial: Record<number, string | null> = {};
      for (const li of proposedTreatmentRows) {
        const row = treatmentRows.find(
          (r) => r.match.matchedAllocationId === li.id,
        );
        initial[li.id] = row?.rowKey ?? null;
      }
      for (const li of proposedPrescriptionRows) {
        const row = prescriptionRows.find(
          (r) => r.match.matchedAllocationId === li.id,
        );
        initial[li.id] = row?.rowKey ?? null;
      }
      return initial;
    },
  );

  // Snapshot of each proposed line item's decision allocation before this form touched anything, so Cancel
  // can restore the exact fields handleAssignmentChange's lump sum patch overwrites, not just total_cost.
  const [originalAllocationValues] = useState<
    Record<
      number,
      {
        allocationType: "treatment" | "prescription";
        unit_price: string;
        units_per_year: number | null;
        units_per_month: number;
        number_of_years: number;
      }
    >
  >(() => {
    const snapshot: Record<
      number,
      {
        allocationType: "treatment" | "prescription";
        unit_price: string;
        units_per_year: number | null;
        units_per_month: number;
        number_of_years: number;
      }
    > = {};
    for (const li of proposedTreatmentRows) {
      const allocation = li.decision_treatment_allocation;
      if (allocation) {
        snapshot[allocation.id] = {
          allocationType: "treatment",
          unit_price: allocation.unit_price,
          units_per_year: allocation.units_per_year,
          units_per_month: allocation.units_per_month,
          number_of_years: allocation.number_of_years,
        };
      }
    }
    for (const li of proposedPrescriptionRows) {
      const allocation = li.decision_prescription_allocation;
      if (allocation) {
        snapshot[allocation.id] = {
          allocationType: "prescription",
          unit_price: allocation.unit_price,
          units_per_year: allocation.units_per_year,
          units_per_month: allocation.units_per_month,
          number_of_years: allocation.number_of_years,
        };
      }
    }
    return snapshot;
  });

  // Allocation ids handleAssignmentChange has actually patched this session,
  // so Cancel only reverts what changed instead of re-patching everything.
  const [touchedAllocationIds, setTouchedAllocationIds] = useState<Set<number>>(
    new Set(),
  );

  // Line items handleAddAsNewItem created this session, so Cancel deletes them outright instead of reverting them.
  const [createdLineItemIds, setCreatedLineItemIds] = useState<number[]>([]);

  // Letter rows added as their own new line item should never appear as a dropdown option again either.
  const [addedAsNewItemRowKeys, setAddedAsNewItemRowKeys] = useState<
    Set<string>
  >(new Set());

  // Disables a row's dropdown/button while its patch is in flight, so a
  // second change can't race the first.
  const [pendingLineItemIds, setPendingLineItemIds] = useState<Set<number>>(
    new Set(),
  );

  const usedTreatmentRowKeys = useMemo(
    () =>
      new Set(
        proposedTreatmentRows
          .map((li) => assignments[li.id])
          .filter((v): v is string => v != null),
      ),
    [proposedTreatmentRows, assignments],
  );
  const usedPrescriptionRowKeys = useMemo(
    () =>
      new Set(
        proposedPrescriptionRows
          .map((li) => assignments[li.id])
          .filter((v): v is string => v != null),
      ),
    [proposedPrescriptionRows, assignments],
  );

  // Letter rows with no matching proposal at all, or whose match is already claimed by another row.
  const newTreatmentRows = useMemo(
    () =>
      treatmentRows.filter(
        (row) =>
          !usedTreatmentRowKeys.has(row.rowKey) &&
          !addedAsNewItemRowKeys.has(row.rowKey),
      ),
    [treatmentRows, usedTreatmentRowKeys, addedAsNewItemRowKeys],
  );
  const newPrescriptionRows = useMemo(
    () =>
      prescriptionRows.filter(
        (row) =>
          !usedPrescriptionRowKeys.has(row.rowKey) &&
          !addedAsNewItemRowKeys.has(row.rowKey),
      ),
    [prescriptionRows, usedPrescriptionRowKeys, addedAsNewItemRowKeys],
  );

  // Any field inside decision_fields can be null when the letter's text didn't match a known label pattern.
  const missingFieldLabels = Object.entries(REVIEWABLE_FIELD_LABELS)
    .filter(([key]) => fields[key as keyof typeof fields] == null)
    .map(([, label]) => label);
  const fieldsIncomplete =
    missingFieldLabels.length === Object.keys(REVIEWABLE_FIELD_LABELS).length;

  // The server recomputes total_cost from unit_price and quantity on every save, so this models the letter
  // amount as a one-time lump sum instead, overwriting those fields rather than just the total.
  const buildLumpSumAllocationPatch = (
    allocationType: "treatment" | "prescription",
    totalCost: string | null,
  ) => {
    const unitPrice = totalCost ?? "0";
    return allocationType === "treatment"
      ? { unit_price: unitPrice, units_per_year: 1, number_of_years: 1 }
      : {
          unit_price: unitPrice,
          units_per_month: 1,
          units_per_year: 1,
          number_of_years: 1,
        };
  };

  const patchAllocation = async (
    allocationId: number,
    allocationType: "treatment" | "prescription",
    totalCost: string | null,
  ) => {
    const patchData = buildLumpSumAllocationPatch(allocationType, totalCost);
    if (allocationType === "treatment") {
      await patchTreatmentAllocation({
        id: allocationId.toString(),
        data: patchData,
      }).unwrap();
    } else {
      await patchPrescriptionAllocation({
        id: allocationId.toString(),
        data: patchData,
      }).unwrap();
    }
  };

  // The backend's auto-match only sets which letter row goes with which line item, never the decision allocation
  // itself, so this applies every initial match once on mount, the same way a manual selection would; no guard
  // against overwriting an existing amount, since the letter's amount should win regardless.
  useEffect(() => {
    let isMounted = true;

    const initialMatches: Array<{
      lineItem: MSADecisionType["line_items"][number];
      allocationType: "treatment" | "prescription";
      rowKey: string;
    }> = [];

    for (const li of proposedTreatmentRows) {
      const rowKey = assignments[li.id];
      if (rowKey) {
        initialMatches.push({
          lineItem: li,
          allocationType: "treatment",
          rowKey,
        });
      }
    }
    for (const li of proposedPrescriptionRows) {
      const rowKey = assignments[li.id];
      if (rowKey) {
        initialMatches.push({
          lineItem: li,
          allocationType: "prescription",
          rowKey,
        });
      }
    }

    if (initialMatches.length === 0) return;

    setPendingLineItemIds((prev) => {
      const next = new Set(prev);
      initialMatches.forEach(({ lineItem }) => next.add(lineItem.id));
      return next;
    });

    // One PATCH per line item rather than a batch endpoint: a CMS letter has at most a few dozen rows, so the
    // extra requests are cheap and each one can fail and toast independently instead of all-or-nothing.
    // Promise.allSettled, not forEach, which doesn't await its callback and
    // could leave an item stuck in pendingLineItemIds forever.
    void Promise.allSettled(
      initialMatches.map(async ({ lineItem, allocationType, rowKey }) => {
        const targetAllocationId =
          allocationType === "treatment"
            ? lineItem.decision_treatment_allocation?.id
            : lineItem.decision_prescription_allocation?.id;
        const rows =
          allocationType === "treatment" ? treatmentRows : prescriptionRows;
        const selectedRow = rows.find((row) => row.rowKey === rowKey);
        const totalCost = selectedRow ? selectedRow.item.total_cost : "0";

        try {
          // Guards the rare case an allocation was deleted before this ran; throwing routes it to the catch
          // below, which toasts instead of failing silently.
          if (!targetAllocationId) {
            throw new Error("Could not find this line item to update");
          }
          await patchAllocation(targetAllocationId, allocationType, totalCost);
          setTouchedAllocationIds((prev) =>
            new Set(prev).add(targetAllocationId),
          );
        } catch {
          // Toast unconditionally, since it's a global overlay, not this
          // component's state, so it's worth showing even after the modal
          // closes.
          KaboodleToaster.error(
            `Failed to auto-apply the matched letter row for "${selectedRow?.item.name || "a line item"}"`,
          );
          if (isMounted) {
            setAssignments((prev) => ({ ...prev, [lineItem.id]: null }));
          }
        } finally {
          if (isMounted) {
            setPendingLineItemIds((prev) => {
              const next = new Set(prev);
              next.delete(lineItem.id);
              return next;
            });
          }
        }
      }),
    );

    // isMounted stops a patch that resolves after the modal closes from updating state.
    return () => {
      isMounted = false;
    };
  }, []);

  // Writes an allocation's amount immediately, in its own request, while the header fields only save when
  // Save Decision is clicked. handleCancelClick below reverts this using originalAllocationValues.
  const handleAssignmentChange = async (
    lineItem: MSADecisionType["line_items"][number],
    allocationType: "treatment" | "prescription",
    newRowKey: string | null,
  ) => {
    const previousRowKey = assignments[lineItem.id] ?? null;
    if (newRowKey === previousRowKey) return;

    const targetAllocationId =
      allocationType === "treatment"
        ? lineItem.decision_treatment_allocation?.id
        : lineItem.decision_prescription_allocation?.id;
    if (!targetAllocationId) {
      KaboodleToaster.error("Could not find this line item to update");
      return;
    }

    const rows =
      allocationType === "treatment" ? treatmentRows : prescriptionRows;
    const selectedRow = newRowKey
      ? rows.find((row) => row.rowKey === newRowKey)
      : null;
    // A blank selection clears the decision amount, so un-picking a row undoes it instead of leaving a stale value.
    const totalCost = selectedRow ? selectedRow.item.total_cost : "0";

    setAssignments((prev) => ({ ...prev, [lineItem.id]: newRowKey }));
    setPendingLineItemIds((prev) => new Set(prev).add(lineItem.id));
    try {
      await patchAllocation(targetAllocationId, allocationType, totalCost);
      setTouchedAllocationIds((prev) => new Set(prev).add(targetAllocationId));
    } catch (error) {
      setAssignments((prev) => ({ ...prev, [lineItem.id]: previousRowKey }));
      KaboodleToaster.error(
        error?.data?.detail || "Failed to update this line item",
      );
    } finally {
      setPendingLineItemIds((prev) => {
        const next = new Set(prev);
        next.delete(lineItem.id);
        return next;
      });
    }
  };

  // Left to throw rather than catching its own errors, since handleSaveDecision calls these through Promise.all
  // and needs a real rejection to know a row failed; it also skips its own success toast, leaving that to
  // handleSaveDecision's overall outcome.
  const handleAddAsNewItem = async (
    rowKey: string,
    allocationType: "treatment" | "prescription",
    item: MSADecisionReviewLetterLineItem,
  ) => {
    const created = await createDecisionLineItem({
      data: {
        msa_decision: decision.id,
        allocation_type: allocationType,
        name: item.name,
      },
    }).unwrap();

    // Claim the row before the amount patch below, so a retry can't create a second line item for it.
    setAddedAsNewItemRowKeys((prev) => new Set(prev).add(rowKey));
    // Tracked regardless of whether the amount patch below succeeds, since the line item exists either way.
    setCreatedLineItemIds((prev) => [...prev, created.id]);

    const createdAllocationId =
      allocationType === "treatment"
        ? created.decision_treatment_allocation?.id
        : created.decision_prescription_allocation?.id;
    if (!createdAllocationId) {
      throw new Error(`Added "${item.name}" but could not set its amount`);
    }

    await patchAllocation(createdAllocationId, allocationType, item.total_cost);
  };

  // MaskedInput's dollar mask stores a formatted string (e.g. "$50,924.00"), so this always needs stripping
  // first. null means the field was cleared, 0 means the reviewer typed zero; both are valid.
  const parseMaskedDollarValue = (value: string): number | null => {
    if (!value) return null;
    const numericValue = parseFloat(value.replace(/[^0-9.-]+/g, ""));
    return isNaN(numericValue) ? null : numericValue;
  };

  const handleSaveDecision = async () => {
    try {
      setIsSaving(true);

      // Unmatched rows become real line items here at save, not when they first show up unmatched, since the
      // reviewer might still change dropdowns first; retrying after a partial failure is safe since
      // addedAsNewItemRowKeys already excludes claimed rows from double-creation.
      await Promise.all([
        ...newTreatmentRows.map((row) =>
          handleAddAsNewItem(row.rowKey, "treatment", row.item),
        ),
        ...newPrescriptionRows.map((row) =>
          handleAddAsNewItem(row.rowKey, "prescription", row.item),
        ),
      ]);

      await patchMSADecision({
        msaId,
        decisionId: decision.id.toString(),
        data: {
          decision_type: decisionType,
          decision_date: decisionDate,
          amount_total: parseMaskedDollarValue(amountTotal),
          medical_total: parseMaskedDollarValue(medicalTotal),
          rx_total: parseMaskedDollarValue(rxTotal),
          cms_rationale: cmsRationale,
          // Tells the backend to file (or retag) the letter as a Document from decision_type in this same request.
          confirm_ocr_letter: true,
        },
      }).unwrap();
      KaboodleToaster.success("Decision saved successfully");
      onSaved();
    } catch (error) {
      // error is a plain Error (message only, no .data) when it came from handleAddAsNewItem above.
      KaboodleToaster.error(
        error?.data?.detail || error?.message || "Failed to save decision",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Reverts existing allocations to their pre-review values and deletes any line item created this session.
  // Still calls onCancel in finally even if a revert fails, since the modal must close either way. Only reverts
  // local edits; the job itself is untouched, so it still blocks other scans on this file until saved.
  const handleCancelClick = async () => {
    setIsCanceling(true);
    try {
      await Promise.all([
        ...Array.from(touchedAllocationIds).map((allocationId) => {
          const original = originalAllocationValues[allocationId];
          if (!original) return Promise.resolve();
          const { allocationType, ...rawFields } = original;
          return allocationType === "treatment"
            ? patchTreatmentAllocation({
                id: allocationId.toString(),
                data: {
                  unit_price: rawFields.unit_price,
                  units_per_year: rawFields.units_per_year,
                  number_of_years: rawFields.number_of_years,
                },
              }).unwrap()
            : patchPrescriptionAllocation({
                id: allocationId.toString(),
                data: {
                  unit_price: rawFields.unit_price,
                  units_per_month: rawFields.units_per_month,
                  units_per_year: rawFields.units_per_year,
                  number_of_years: rawFields.number_of_years,
                },
              }).unwrap();
        }),
        ...createdLineItemIds.map((id) =>
          deleteMsaDecisionLineItemById({ id }).unwrap(),
        ),
      ]);
    } catch {
      KaboodleToaster.error(
        "Could not fully undo changes made during review. Some amounts may remain updated.",
      );
    } finally {
      setIsCanceling(false);
      onCancel();
    }
  };

  // Every letter row not already claimed by a different proposed row, plus whichever row is currently selected.
  const buildRowOptions = (
    rows: LetterRow[],
    usedRowKeys: Set<string>,
    currentRowKey: string | null,
  ): OptionType[] =>
    rows
      .filter(
        (row) => row.rowKey === currentRowKey || !usedRowKeys.has(row.rowKey),
      )
      .map((row) => ({
        value: row.rowKey,
        label: `${row.item.name} — ${formatCurrency(row.item.total_cost)}`,
      }));

  const renderProposedRow = (
    lineItem: MSADecisionType["line_items"][number],
    allocationType: "treatment" | "prescription",
    name: string | undefined,
    proposedTotal: string | number | null | undefined,
    rows: LetterRow[],
    usedRowKeys: Set<string>,
  ) => {
    const currentRowKey = assignments[lineItem.id] ?? null;
    const options = buildRowOptions(rows, usedRowKeys, currentRowKey);
    const selectedOption =
      options.find((option) => option.value === currentRowKey) || null;
    const isPending = pendingLineItemIds.has(lineItem.id);
    const currentRow = rows.find((row) => row.rowKey === currentRowKey);
    const isFuzzyMatch = currentRow?.match.matchType === "name_fuzzy";

    return (
      <div
        className="data-values ocr-line-item-row"
        key={`${allocationType}-li-${lineItem.id}`}
      >
        <div className="ocr-proposed-cell">
          <label>{name}</label>
          <span>{formatCurrency(proposedTotal)}</span>
        </div>
        <div className="ocr-decision-cell">
          <Select
            name={`ocr-decision-for-${lineItem.id}`}
            options={options}
            value={selectedOption}
            isClearable
            readOnly={isPending}
            isLoading={isPending}
            onChange={(option: OptionType | null) =>
              handleAssignmentChange(
                lineItem,
                allocationType,
                option?.value != null ? String(option.value) : null,
              )
            }
            placeholder="-- Select --"
          />
          {isFuzzyMatch && (
            <span className="ocr-fuzzy-match-note">
              Matched by name similarity, please verify.
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="OCRMSADecisionReviewForm">
      {fieldsIncomplete && (
        <div className="data-values">
          <p>
            Could not read decision fields from the letter automatically. Please
            enter them by hand below.
          </p>
        </div>
      )}
      {!fieldsIncomplete && (
        <div className="data-values">
          <p>
            <b>Letter identity</b>, check this against the file above before
            saving:
          </p>
          <p>
            {fields.claimant_name || "unknown claimant"} &middot; Case Control{" "}
            {fields.case_control_number || "unknown"} &middot; Medicare ID{" "}
            {fields.medicare_id || "unknown"} &middot; DOI{" "}
            {fields.date_of_injury || "unknown"}
          </p>
          {missingFieldLabels.length > 0 && (
            <p>
              Could not read the following from the letter automatically, please
              check by hand: {missingFieldLabels.join(", ")}.
            </p>
          )}
        </div>
      )}
      <div className="data-values">
        <label>Type:</label>
        <Select
          name="ocr-decision-type"
          options={constants?.CMS_MSA_DECISION_TYPE || []}
          value={
            constants?.CMS_MSA_DECISION_TYPE?.find(
              (option) => option.value === decisionType,
            ) || null
          }
          onChange={(option: OptionType) =>
            setDecisionType(option?.value != null ? String(option.value) : null)
          }
          placeholder="Select type"
        />

        <label>Decision Date:</label>
        <DatePicker
          name="ocr-decision-date"
          onDateChange={(date) => setDecisionDate(format(date, "yyyy-MM-dd"))}
          initialDate={decisionDate ? STRING_TO_DATE(decisionDate) : undefined}
        />

        <label>Approval Amount Total:</label>
        <MaskedInput
          name="ocr-amount-total"
          mask="dollar"
          value={amountTotal}
          onChange={(event: { target: { value: string } }) =>
            setAmountTotal(event.target.value)
          }
        />

        <label>Medical Total:</label>
        <MaskedInput
          name="ocr-medical-total"
          mask="dollar"
          value={medicalTotal}
          onChange={(event: { target: { value: string } }) =>
            setMedicalTotal(event.target.value)
          }
        />

        <label>Rx Total:</label>
        <MaskedInput
          name="ocr-rx-total"
          mask="dollar"
          value={rxTotal}
          onChange={(event: { target: { value: string } }) =>
            setRxTotal(event.target.value)
          }
        />

        <label>CMS Rationale:</label>
        <TextArea
          name="ocr-cms-rationale"
          label=""
          value={cmsRationale}
          onChange={(value: string | React.ChangeEvent<HTMLTextAreaElement>) =>
            setCmsRationale(
              typeof value === "string" ? value : value.target.value,
            )
          }
          placeholder="Enter rationale"
        />
      </div>

      {proposedTreatmentRows.length > 0 && (
        <>
          <div className="data-values">
            <label>
              <b>Treatment: proposed vs. letter decision</b>
            </label>
          </div>
          <div className="data-values ocr-line-item-row ocr-column-headers">
            <div className="ocr-proposed-cell">
              <label>Proposed</label>
            </div>
            <div className="ocr-decision-cell">
              <label>Letter Decision</label>
            </div>
          </div>
          {proposedTreatmentRows.map((lineItem) =>
            renderProposedRow(
              lineItem,
              "treatment",
              lineItem.proposed_treatment_allocation?.name,
              lineItem.proposed_treatment_allocation?.total_cost,
              treatmentRows,
              usedTreatmentRowKeys,
            ),
          )}
        </>
      )}

      {newTreatmentRows.length > 0 && (
        <>
          <div className="data-values">
            <label>
              <b>Additional items from letter (no matching proposal)</b>
            </label>
            <span>Added to the decision automatically when you save.</span>
          </div>
          {newTreatmentRows.map(({ item, rowKey }) => (
            <div className="data-values ocr-line-item-row" key={rowKey}>
              <label>{item.name}</label>
              <span>{formatCurrency(item.total_cost)}</span>
            </div>
          ))}
        </>
      )}

      {decisionOnlyTreatmentRows.length > 0 && (
        <>
          <div className="data-values">
            <label>
              <b>Added to decision (no proposal)</b>
            </label>
          </div>
          {decisionOnlyTreatmentRows.map((lineItem) => (
            <div
              className="data-values ocr-line-item-row"
              key={`treatment-decision-only-${lineItem.id}`}
            >
              <label>{lineItem.decision_treatment_allocation?.name}</label>
              <span>
                {formatCurrency(
                  lineItem.decision_treatment_allocation?.total_cost,
                )}
              </span>
            </div>
          ))}
        </>
      )}

      {proposedPrescriptionRows.length > 0 && (
        <>
          <div className="data-values">
            <label>
              <b>Prescriptions: proposed vs. letter decision</b>
            </label>
          </div>
          <div className="data-values ocr-line-item-row ocr-column-headers">
            <div className="ocr-proposed-cell">
              <label>Proposed</label>
            </div>
            <div className="ocr-decision-cell">
              <label>Letter Decision</label>
            </div>
          </div>
          {proposedPrescriptionRows.map((lineItem) =>
            renderProposedRow(
              lineItem,
              "prescription",
              lineItem.proposed_prescription_allocation?.name,
              lineItem.proposed_prescription_allocation?.total_cost,
              prescriptionRows,
              usedPrescriptionRowKeys,
            ),
          )}
        </>
      )}

      {newPrescriptionRows.length > 0 && (
        <>
          <div className="data-values">
            <label>
              <b>Additional prescriptions from letter (no matching proposal)</b>
            </label>
            <span>Added to the decision automatically when you save.</span>
          </div>
          {newPrescriptionRows.map(({ item, rowKey }) => (
            <div className="data-values ocr-line-item-row" key={rowKey}>
              <label>
                {item.name} {item.ndc ? `(NDC ${item.ndc})` : ""}
              </label>
              <span>{formatCurrency(item.total_cost)}</span>
            </div>
          ))}
        </>
      )}

      {decisionOnlyPrescriptionRows.length > 0 && (
        <>
          <div className="data-values">
            <label>
              <b>Added to decision (no proposal)</b>
            </label>
          </div>
          {decisionOnlyPrescriptionRows.map((lineItem) => (
            <div
              className="data-values ocr-line-item-row"
              key={`prescription-decision-only-${lineItem.id}`}
            >
              <label>{lineItem.decision_prescription_allocation?.name}</label>
              <span>
                {formatCurrency(
                  lineItem.decision_prescription_allocation?.total_cost,
                )}
              </span>
            </div>
          ))}
        </>
      )}

      {!decisionType && (
        <div className="data-values">
          <p>
            Select a decision type above before saving, so the letter gets
            filed.
          </p>
        </div>
      )}
      <div className="flex jc-c">
        <Button
          onClick={handleCancelClick}
          disabled={isSaving || isCanceling || pendingLineItemIds.size > 0}
        >
          {isCanceling ? "Canceling..." : "Cancel"}
        </Button>
        <Button
          onClick={handleSaveDecision}
          disabled={
            isSaving ||
            isCanceling ||
            pendingLineItemIds.size > 0 ||
            !decisionType
          }
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default OCRMSADecisionReviewForm;
